/**
 * Watchlist entity types
 *
 * Defines types for the persistent watchlist feature that allows users
 * to favorite pairs, arbitrages, and balancers.
 *
 * Uses composite keys to prevent ID collisions:
 * - Pairs: `${projectId}-${pairId}`
 * - Arbitrages: `${projectId}-${arbitrageId}`
 * - Balancers: `${projectId}-${balancerId}`
 */

export type WatchlistItemType = 'pair' | 'arbitrage' | 'balancer';

/**
 * Create composite key for watchlist items
 */
export function createWatchlistKey(projectId: number, itemId: number): string {
  return `${projectId}-${itemId}`;
}

/**
 * Parse composite key back to project and item IDs
 */
export function parseWatchlistKey(key: string): { projectId: number; itemId: number } | null {
  const parts = key.split('-');
  if (parts.length !== 2) return null;
  const projectId = parseInt(parts[0], 10);
  const itemId = parseInt(parts[1], 10);
  if (isNaN(projectId) || isNaN(itemId)) return null;
  return { projectId, itemId };
}

export interface WatchlistState {
  /** Array of favorited pair keys (format: "projectId-pairId") */
  favoritePairs: string[];

  /** Array of favorited arbitrage keys (format: "projectId-arbitrageId") */
  favoriteArbitrages: string[];

  /** Array of favorited balancer keys (format: "projectId-balancerId") */
  favoriteBalancers: string[];

  /** Array of favorited project page IDs (just projectId as string) */
  favoriteProjects: string[];
}

export interface ProjectItems {
  projectId: number;
  pairKeys: string[];
  arbitrageKeys: string[];
  balancerKeys: string[];
}

export interface WatchlistActions {
  /** Toggle a pair's favorite status */
  toggleFavoritePair: (projectId: number, pairId: number) => void;

  /** Toggle an arbitrage's favorite status */
  toggleFavoriteArbitrage: (projectId: number, arbitrageId: number) => void;

  /** Toggle a balancer's favorite status */
  toggleFavoriteBalancer: (projectId: number, balancerId: number) => void;

  /** Toggle a project page's favorite status (All pairs link) */
  toggleFavoriteProject: (projectId: number) => void;

  /** Add all items from a project to watchlist */
  addProjectItems: (items: ProjectItems) => void;

  /** Remove all items from a project from watchlist */
  removeProjectItems: (items: ProjectItems) => void;

  /** Check if a pair is favorited */
  isFavoritePair: (projectId: number, pairId: number) => boolean;

  /** Check if an arbitrage is favorited */
  isFavoriteArbitrage: (projectId: number, arbitrageId: number) => boolean;

  /** Check if a balancer is favorited */
  isFavoriteBalancer: (projectId: number, balancerId: number) => boolean;

  /** Check if a project page is favorited */
  isFavoriteProject: (projectId: number) => boolean;

  /** Clear all favorites */
  clearAll: () => void;
}

export type WatchlistStore = WatchlistState & WatchlistActions;
